import { createSelector } from 'reselect';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';

import {
  CHATBOT_QUERY_PARAM_MURABAHA,
  CHATBOT_WINDOW_STATUS_ERROR,
  CLIENT_FEATURES_SSO_HIDE_LOGOUT_BUTTON,
  DEFAULT_CLIENT_THEME_COLORS,
  DEFAULT_DESKTOP_THEME_COLORS,
  DEFAULT_FONT,
  DEFAULT_MOBILE_THEME_COLORS,
  DOC_UPLOAD_STATUS_TITLE_DICT,
  GENERIC_CUSTOMER_SERVICE_PHONE_NUMBER,
  MOST_RECENT_FAILED_UPLOAD,
  POST_TRANSFER_BANNER_TEXT,
  POST_TRANSFER_RESPA_BANNER_TEXT,
  PRELOGIN_ALERT_BANNER_PROPS,
  PRE_TRANSFER_BANNER_TEXT,
  PRE_TRANSFER_RESPA_BANNER_TEXT,
  SPLIT_OUTAGE_BANNER,
  SPLIT_SSO_PRETRANSFER,
  SPLIT_UNAVAILABLE_FEATURES,
  SPLIT_ZENDESK_CHATBOT,
  STATE_ABBR_DICTIONARY,
} from './constants';
import makeSelectMobile, { makeSelectBuildType } from '../Mobile/selectors';
import { LANGUAGE_ENGLISH_MURABAHA } from '../../utils/constants';
import {
  capitalizeString,
  convertNumToCurrencyStr,
  formatDate,
  formatPhoneNumber,
  interpolate,
} from '../../utils/globalHelpers';
import { initialState } from './reducer';
import { getTopAndBottomPreLoginBanners, sortLoansByOnlineAvailabilityStatus } from './helpers';

/**
 * Direct selector to the main state domain
 */
const selectMainDomain = (state) => state.main || initialState;

/**
 * Direct selector to the intl object
 */
const selectIntl = (_, props) => props.intl;

const getAccountModalProps = () => createSelector(
  makeSelectMain('loans'),
  makeSelectMain('selectedLoan'),
  getProfileCurrentSetting(),
  (
    loans,
    selectedLoan,
    { username },
  ) => {
    if (loans.length) {
      const selectedLoanData =
        loans.find(({ hashedLoanNumber }) => hashedLoanNumber === selectedLoan);
      if (selectedLoanData.header) {
        const { header: { address1 = '', address2 = '' } = {} } = selectedLoanData || {};
        return {
          address1,
          address2,
          username,
        };
      }
    }
    return {};
  },
);

const getChatbotQueryParams = () => createSelector(
  makeSelectMain('loans'),
  (loans) => {
    const isAnyLoanMurabaha =
      loans.some(({ additionalInformation: { isMurabaha } }) => isMurabaha === 'true');

    return `${CHATBOT_QUERY_PARAM_MURABAHA}=${isAnyLoanMurabaha}`;
  },
);

const getHasMultiLoans = () => createSelector(
  makeSelectMain('loans'),
  (loans) => loans.length > 1,
);

const getIsLoanAvailable = () => createSelector(
  getSelectedLoanData(),
  ({ onlineAvailabilityStatus }) => onlineAvailabilityStatus === 'Available',
);

const getIsSso = () => createSelector(
  makeSelectMain('clientInfo'),
  makeSelectMain('profile'),
  ({ clientAuthentication }, { currentSetting }) => {
    // post auth SSO check
    if (!isEmpty(currentSetting)) {
      const { authenticatedBy } = currentSetting;
      return authenticatedBy === 'SSO';
    }
    // pre-auth SSO check
    if (!isEmpty(clientAuthentication)) {
      const { connectionInformation, grantType } = clientAuthentication;
      if (grantType === 'sso_saml' && connectionInformation) {
        const { connectionMethod, redirectMode } = connectionInformation;
        if (connectionMethod && redirectMode) {
          return true;
        }
      } else if (grantType === 'sso_oidc' || grantType === 'sso_sslcertify') {
        return true;
      }
    }
    return false;
  },
);

const getSsoType = () => createSelector(
  makeSelectMain('clientInfo'),
  ({ clientAuthentication }) => {
    if (!!clientAuthentication && !isEmpty(clientAuthentication)) {
      const { connectionInformation, grantType } = clientAuthentication;
      if (connectionInformation && grantType && grantType === 'sso_saml') {
        const { connectionMethod, redirectMode } = connectionInformation;
        /**
         * Retrospective note: It's unclear why we're bothering to check the connectionMethod at all
         * here. Seems likely either (1) legacy from early days of devs just not knowing all the use
         * cases (eg, maybe the initial SAML clients were flagged as Server+IDP and POST+SP and so
         * devs thought that those were the only possible combos?), or (2) connectionMethod could maybe
         * (accidentally?) be undefined for certain clients? In any case, it doesn't seem like it's
         * to filter out if connectionMethod is Server vs POST.
         */
        if ((connectionMethod === 'Server' || connectionMethod === 'POST') && redirectMode === 'IDP') return 'samlIDP';
        if (connectionMethod === 'POST' && redirectMode === 'SP') return 'samlSP';
      }
      if (grantType === 'sso_oidc') return 'oidc';
      if (grantType === 'sso_sslcertify') return 'ssl';
    }
    return '';
  },
);

const getWebSsoLogoutType = () => createSelector(
  makeSelectMain('clientInfo'),
  ({ clientFeaturesWeb: { logoutType } = {} }) => logoutType,
);

const getPaymentMethod = () => createSelector(
  makeSelectMain('loanDetails'),
  ({ summary: { paymentMethod } }) => paymentMethod.trim(),
);

const getLoansIsPrimaryStatus = () => createSelector(
  makeSelectMain('loans'),
  (loans) => loans.reduce((acc, { hashedLoanNumber, isPrimaryLoan }) => {
    acc[hashedLoanNumber] = isPrimaryLoan;
    return acc;
  }, {}),
);

const getMainClientAuthProps = (prop) => createSelector(
  getIsSso(),
  makeSelectMain('clientInfo'),
  (isSso, { clientAuthentication }) => {
    if (isSso &&
      (
        clientAuthentication.grantType === 'sso_saml' ||
        clientAuthentication.grantType === 'sso_oidc'
      )) {
      return clientAuthentication[prop] ||
      clientAuthentication.connectionInformation[prop] ||
      (clientAuthentication.connectionInformation.parameters &&
        clientAuthentication.connectionInformation.parameters[prop]);
    }
    if (isSso && clientAuthentication.grantType === 'sso_sslcertify') {
      return clientAuthentication[prop] || clientAuthentication.connectionInformation[prop];
    }
    return '';
  },
);

const getProfileCurrentSetting = () => createSelector(
  makeSelectMain('profile'),
  ({ currentSetting }) => currentSetting || { firstName: '', lastName: '' },
);

const getMainClientInfo = (prop) => createSelector(
  makeSelectMain('clientInfo'),
  (clientInfoState) => clientInfoState[prop],
);

const getClientFontToUse = () => createSelector(
  makeSelectMain('clientInfo'),
  makeSelectMobile('isNativeMobile'),
  (clientInfoState, isNativeMobile) => {
    const { fonts } = clientInfoState;
    if (!isEmpty(fonts) && !isNativeMobile) {
      const { primaryFont, secondaryFont } = fonts;
      return {
        primaryFont: primaryFont || DEFAULT_FONT,
        secondaryFont: secondaryFont || DEFAULT_FONT,
      };
    }

    return {
      primaryFont: DEFAULT_FONT,
      secondaryFont: DEFAULT_FONT,
    };
  },
);

/**
 * @description Selects the main error for screen reader
 */
const getMainNotifications = () => createSelector(
  getError('main'),
  (error) => {
    const notifications = [];
    if (error) { notifications.push(error); }
    return notifications;
  },
);

const getMostRecentDocUploadNotificationFailure = () => createSelector(
  makeSelectMain('loans'),
  makeSelectMain('selectedLoan'),
  makeSelectMain('dismissedDocUploadStatusBanners'),
  (loans, selectedLoan, dismissedDocUploadStatusBanners) => {
    const { additionalInformation: { uploadedDocumentStatus } = {} } =
      loans.find(({ hashedLoanNumber }) => hashedLoanNumber === selectedLoan) || {};

    const { availableDocuments } = uploadedDocumentStatus || {};
    if (!availableDocuments) return null;

    const mostRecentDocUploadNotificationFailure = availableDocuments
      .filter(({ documentId, status }) => (
        status === 'Failed'
        && !dismissedDocUploadStatusBanners.includes(documentId)
      )).sort((a, b) => moment(b.updatedAt).diff(moment(a.updatedAt)))[0];

    return mostRecentDocUploadNotificationFailure;
  },
);

/**
 * @description Select and format the currently selected loan address from Main store
 * @param {boolean} shortAddress optional - returns address with trimmed formatting
 * @returns formatted loan address
 */
const getSelectedLoanAddress = (shortAddress = false) => createSelector(
  getSelectedLoanData(),
  ({ header }) => {
    if (header) {
      const {
        address1 = '',
        city = '',
        postalCode = '',
        stateCode = '',
      } = header;
      const stateAndPostalCode = `${stateCode || ''} ${postalCode || ''}`.trim();

      return shortAddress ?
        `${capitalizeString(address1)}${postalCode ? `, ${postalCode}` : ''}` :
        // eslint-disable-next-line max-len
        `${capitalizeString(address1)}${city ? `, ${capitalizeString(city)}` : ''}${stateAndPostalCode ? `, ${stateAndPostalCode}` : ''}`;
    }

    return '';
  },
);

/**
 * @description Select and format loan address from Main store
 * @returns formatted loan address
 */
const getLoanAddresses = () => createSelector(
  makeSelectMain('loans'),
  (loans) => {
    const formattedLoans = loans.map(({
      hashedLoanNumber,
      header,
      isPrimaryLoan,
      maskedLoanNumber,
      onlineAvailabilityStatus,
    }) => {
      if (header) {
        const {
          address1 = '',
          city = '',
          postalCode = '',
          stateCode = '',
        } = header;
        const stateAndPostalCode = `${stateCode || ''} ${postalCode || ''}`.trim();
        const addressFirstLine = `${capitalizeString(address1)}`;
        const addressSecondLine =
          `${capitalizeString(city)}${stateAndPostalCode ? `, ${stateAndPostalCode}` : ''}`;

        return ({
          isPrimaryLoan,
          loan: hashedLoanNumber,
          maskedLoanNumber,
          onlineAvailabilityStatus,
          property: {
            addressFirstLine,
            addressSecondLine,
          },
        });
      }
      return ({
        isPrimaryLoan,
        loan: hashedLoanNumber,
        maskedLoanNumber,
        onlineAvailabilityStatus,
        property: {},
      });
    });
    return sortLoansByOnlineAvailabilityStatus(formattedLoans);
  },
);

const getInactiveLoanRedirectUrl = () => createSelector(
  makeSelectMain('loans'),
  makeSelectMain('selectedLoan'),
  (loans, selectedLoan) => {
    const selectedLoanData =
      loans.find(({ hashedLoanNumber }) => hashedLoanNumber === selectedLoan) || {};
    return selectedLoanData.inactiveRedirectUrl || '';
  },
);

const getNextPaymentDueDetails = () => createSelector(
  makeSelectMain('loanDetails'),
  (loanDetails) => {
    const {
      outstandingLateCharges,
      outstandingOtherFees,
      payment,
    } = loanDetails;
    const { nextPaymentDueDate = '', totalMonthlyPayment = 0 } = payment || {};
    const totalPayment = totalMonthlyPayment + outstandingLateCharges + outstandingOtherFees;
    return {
      nextPaymentDueDate,
      regularMonthlyPayment: totalMonthlyPayment,
      totalPaymentDue: totalPayment,
    };
  },
);

const getSelectedLoanData = () => createSelector(
  makeSelectMain('loans'),
  makeSelectMain('selectedLoan'),
  (loans, selectedLoan) =>
    loans.find(({ hashedLoanNumber }) => hashedLoanNumber === selectedLoan) || {},
);

const getSelectedLoanMaskedLoanNumber = () => createSelector(
  getSelectedLoanData(),
  ({ maskedLoanNumber }) => (maskedLoanNumber || ''),
);

const getSelectedLoanUnmaskedLoanNumber = () => createSelector(
  getSelectedLoanData(),
  ({ loanNumber }) => (loanNumber || ''),
);

const getSelectLoanProps = () => createSelector(
  makeSelectMain('loans'),
  getUserDisplayName(),
  (loans, userDisplayName) => {
    const formattedLoans = loans.reduce((acc, {
      hashedLoanNumber,
      header,
      isPrimaryLoan,
      maskedLoanNumber,
      nextPaymentDueDate,
      onlineAvailabilityStatus,
      totalMonthlyPayment,
    }) => {
      let loanObj = {};
      if (onlineAvailabilityStatus !== 'Available') {
        loanObj = {
          maskedLoanNumber,
          onlineAvailabilityStatus,
        };
      } else {
        const { address1 } = header;
        const nextPaymentDueDateMessage = nextPaymentDueDate ?
          `Due on ${formatDate(nextPaymentDueDate, 'UTC', 'MMMM D, YYYY')}` :
          'No Payment Due';
        loanObj = {
          address: address1,
          hashedLoanNumber,
          isPrimaryLoan,
          maskedLoanNumber,
          nextPaymentDueDateMessage,
          onlineAvailabilityStatus,
          totalMonthlyPayment: convertNumToCurrencyStr(totalMonthlyPayment),
        };
      }
      acc.push(loanObj);
      return acc;
    }, []);
    const sortedLoans = sortLoansByOnlineAvailabilityStatus(formattedLoans);
    return { loans: sortedLoans, userDisplayName };
  },
);

const selectBannerError = () => createSelector(
  getError('respaLetter'),
  getError('chatbot'),
  (respaLetterError, chatbotError) => respaLetterError || chatbotError,
);

const selectClientDesktopFeatures = () => createSelector(
  selectShowLogoutButton(),
  (showLogoutButton) => ({ showLogoutButton }),
);

const selectClientMobileFeatures = () => createSelector(
  selectMobileShowEditPassword(),
  selectShowLogoutButton(),
  selectMobileShowSecurityCard(),
  (
    showEditPassword,
    showLogoutButton,
    showSecurityCard,
  ) => ({
    showEditPassword,
    showLogoutButton,
    showSecurityCard,
  }),
);

const selectCustomerServicePhoneNumber = () => createSelector(
  getMainClientInfo('customerServicePhoneNumber'),
  (customerServicePhoneNumber) =>
    customerServicePhoneNumber || GENERIC_CUSTOMER_SERVICE_PHONE_NUMBER,
);

const selectDesktopThemeBorders = () => createSelector(
  getMainClientInfo('colors'),
  (colors) => {
    const { error, primary } = { ...DEFAULT_CLIENT_THEME_COLORS, ...colors };
    const { buttonGray, primaryGray } = DEFAULT_DESKTOP_THEME_COLORS;
    return {
      boldErrorBorder: `0.3rem solid ${error}`,
      dashedBorder: `0.1rem dashed ${primaryGray}`,
      errorBorder: `0.1rem solid ${error}`,
      primaryBorder: `0.1rem solid ${primary}`,
      secondaryBorder: `0.1rem solid ${primaryGray}`,
      tertiaryBorder: `0.1rem solid ${buttonGray}`,
    };
  },
);

const selectDesktopThemeColors = () => createSelector(
  getMainClientInfo('colors'),
  (colors) => {
    const {
      desktopHeaderBackgroundPrelogin,
      error,
      footerBackground,
      footerIconColor,
      footerLinkText,
      footerLinkTextHover,
      footerText,
      linkText,
      linkTextHover,
      navigationMenuActive,
      primary,
      primaryButton,
      primaryButtonHover,
      primaryButtonText,
      primaryButtonTextHover,
      registerButtonBackgroundPrelogin,
      registerButtonHoverPrelogin,
      registerButtonTextPrelogin,
      svgHighlight,
    } = { ...DEFAULT_CLIENT_THEME_COLORS, ...colors };
    return {
      ...DEFAULT_DESKTOP_THEME_COLORS,
      accentRed: error,
      desktopHeaderBackgroundPrelogin,
      error,
      footerBackground,
      footerIconColor,
      footerLinkText,
      footerLinkTextHover,
      footerText,
      linkText,
      linkTextHover,
      navigationMenuActive,
      primary,
      primaryButton: primaryButton || primary,
      primaryButtonHover,
      primaryButtonText,
      primaryButtonTextHover,
      registerButtonBackgroundPrelogin,
      registerButtonHoverPrelogin,
      registerButtonTextPrelogin,
      svgHighlight,
    };
  },
);

const selectMobileShowEditPassword = () => createSelector(
  getIsSso(),
  makeSelectMain('isSslAuth'),
  (isSso, isSslAuth) => (!isSso && !isSslAuth),
);

const selectMobileShowSecurityCard = () => createSelector(
  selectMobileShowEditPassword(),
  makeSelectMobile('isNativeMobile'),
  (showEditPassword, isNativeMobile) => ((isNativeMobile || showEditPassword)),
);

const selectMobileThemeColors = () => createSelector(
  getMainClientInfo('colors'),
  makeSelectMobile('isNativeMobile'),
  (colors, isNativeMobile) => {
    const {
      error,
      footerBackground,
      footerIconColor,
      footerLinkText,
      footerText,
      headerBackground,
      linkText,
      navigationMenuActive,
      primary,
      primaryButton,
      primaryButtonHover,
      primaryButtonText,
      primaryButtonTextHover,
      svgHighlight,
      useDefaultMobileButtonOnPress,
    } = { ...DEFAULT_CLIENT_THEME_COLORS, ...colors };
    const underlayColor = useDefaultMobileButtonOnPress ? undefined : primaryButtonHover;

    if (isNativeMobile) {
      return {
        ...DEFAULT_MOBILE_THEME_COLORS,
        ...DEFAULT_CLIENT_THEME_COLORS,
        headerBackground,
      };
    }

    return {
      ...DEFAULT_MOBILE_THEME_COLORS,
      accentRed: error,
      footerBackground,
      footerIconColor,
      footerLinkText,
      footerText,
      headerBackground,
      linkText,
      navigationMenuActive,
      primary,
      primaryButton: primaryButton || primary,
      primaryButtonHover: underlayColor,
      primaryButtonText,
      primaryButtonTextHover,
      svgHighlight,
    };
  },
);

const selectHasChapterSevenBankruptcy = () => createSelector(
  makeSelectMain('isLoggedIn'),
  makeSelectMain('loanDetails'),
  (isLoggedIn, { summary }) => {
    if (!isLoggedIn) return true;
    if (!!summary && summary.bankruptcyChapter7Code !== 'None') return true;
    return false;
  },
);

const selectHasUnreadDocUploadStatusNotifications = () => createSelector(
  makeSelectMain('loans'),
  makeSelectMain('selectedLoan'),
  getProfileCurrentSetting(),
  (loans, selectedLoan, { lastLoginDate }) => {
    const { additionalInformation: { uploadedDocumentStatus } = {} } =
      loans.find(({ hashedLoanNumber }) => hashedLoanNumber === selectedLoan) || {};
    const { availableDocuments } = uploadedDocumentStatus || {};
    if (!availableDocuments) return false;

    return availableDocuments.some(({ updatedAt }) => moment(updatedAt).isAfter(lastLoginDate));
  },
);

/**
 * @description Selects the view name that has precedence/priority from the account's
 *              viable modal views, to only display once after login.
 */
const selectInstantPostLoginModalViewName = () => createSelector(
  selectIsPaperlessBillingModalAllowed(),
  selectIsSsoPretransferModalAllowed(),
  selectIsNYCLanguagePreferenceModalRequired(),
  (
    isPaperlessBillingModalAllowed,
    isSsoPretransferModalAllowed,
    isNYCLanguagePreferenceModalRequired,
  ) => {
    if (isNYCLanguagePreferenceModalRequired) {
      return 'languagePreference';
    }
    if (isSsoPretransferModalAllowed) {
      return 'ssoPretransfer';
    }
    if (isPaperlessBillingModalAllowed) {
      return 'paperlessBilling';
    }
    return '';
  },
);

const selectInstantPostLoginModalViewProps = () => createSelector(
  makeSelectMain('instantPostLoginModal'),
  makeSelectMain('isLoggedInViaInitRequest'),
  makeSelectMain('isInactivityModalOpen'),
  selectInstantPostLoginModalViewName(),
  selectIsNYCLanguagePreferenceModalRequired(),
  (
    { isEnabled, step },
    isLoggedInViaInitRequest,
    isInactivityModalOpen,
    modalViewName,
    isNYCLanguagePreferenceModalRequired,
  ) => ({
    isModalViewOpen:
      isEnabled
      && !isInactivityModalOpen
      && (isNYCLanguagePreferenceModalRequired || !isLoggedInViaInitRequest),
    modalViewName,
    modalViewStep: step,
  }),
);

const selectInstantPostLoginModalPaperlessStatus = (prop) => createSelector(
  makeSelectMain('instantPostLoginModal'),
  ({ paperlessStatus }) => paperlessStatus[prop] || '',
);

const selectChatbotWindowStatus = () => createSelector(
  makeSelectMain('chatbotWindowStatus'),
  (chatbotWindowStatus) => chatbotWindowStatus,
);

const selectIsMurabahaAccount = () => createSelector(
  makeSelectMain('internationalization'),
  ({ language }) => language === LANGUAGE_ENGLISH_MURABAHA,
);

const selectIsSsoPretransferModalAllowed = () => createSelector(
  makeSelectMain('treatments'),
  makeSelectMain('sameClientCodes'),
  (treatments, sameClientCodes) => {
    const { treatment: ssoPretransferTreatment } = treatments[SPLIT_SSO_PRETRANSFER] || {};
    return ssoPretransferTreatment === 'on' && sameClientCodes;
  },
);

const selectIsNYCLanguagePreferenceModalRequired = () => createSelector(
  makeSelectMain('instantPostLoginModal'),
  ({ isEnabled, languagePreference: { allowedOperation } }) =>
    allowedOperation === 'Request_Change' && isEnabled,
);

const selectIsPaperlessBillingModalAllowed = () => createSelector(
  selectInstantPostLoginModalPaperlessStatus('paperlessBilling'),
  (allowedOperation) => allowedOperation === 'Request_Enable',
);

const selectLanguage = () => createSelector(
  makeSelectMain('internationalization'),
  ({ language }) => language,
);

/**
 * Get name of user that we display on prominent pages like PaymentsHome and SwitchLoans.
 * @returns - Either first name if exists, or last name as a fallback. (Some accounts do not have
 * a first name.)
 */
const getUserDisplayName = () => createSelector(
  getProfileCurrentSetting(),
  ({ firstName, lastName }) => capitalizeString(firstName || lastName),
);

const selectMainStatus = (prop) => createSelector(
  makeSelectMain('status'),
  (status) => status[prop],
);

const selectPaperlessBillingProps = () => createSelector(
  getSelectedLoanData(),
  getSelectedLoanMaskedLoanNumber(),
  selectMainStatus('putPaperlessSetting'),
  ({ header }, maskedLoanNumber, requestStatus) => {
    const { address1 = '' } = header || {};
    return ({
      confirmationData: {
        loanNumber: maskedLoanNumber.replace(/X/g, '*'),
        propertyAddress: address1,
      },
      requestStatus,
    });
  },
);

const selectLanguagePreferenceProps = () => createSelector(
  makeSelectMain('instantPostLoginModal'),
  selectMainStatus('putLanguagePreference'),
  makeSelectMobile('isMobile'),
  ({ languagePreference: { allowedLanguages } }, requestStatus, isMobile) => ({
    languagePreference: {
      /**
       * Map the correct key for platform, and add index to dropdown values as the
       * values from api are not unique.
       */
      allowedLanguages: allowedLanguages.map(({ name, value }, index) =>
        ({ [isMobile ? 'label' : 'display']: name, value: `${value}-${index}` })),
    },
    requestStatus,
  }),
);

const selectShowLogoutButton = () => createSelector(
  getMainClientInfo('clientCode'),
  makeSelectMain('clientInfo'),
  makeSelectMobile('isNativeMobile'),
  getSsoType(),
  getIsSso(),
  (
    clientCode,
    { clientFeaturesNative, clientFeaturesWeb },
    isNativeMobile,
    ssoType,
    isSso,
  ) => {
    let showLogoutButton = true;
    if (isSso) {
      // TODO: Uncomment code below once all client branches have switched to backend features logic
      // const { hideLogoutButton } = isNativeMobile ? clientFeaturesNative : clientFeaturesWeb;
      // if (hideLogoutButton) {
      //   showLogoutButton = false;
      // }
      // TODO: Remove code below once all client branches have switched to backend features logic
      const platform = isNativeMobile ? 'native' : 'web';
      const clientKey = CLIENT_FEATURES_SSO_HIDE_LOGOUT_BUTTON[clientCode] ? clientCode : 'default';
      const { [clientKey]: { [platform]: ssoTypes } } = CLIENT_FEATURES_SSO_HIDE_LOGOUT_BUTTON;
      const frontendHidesLogoutButton = ssoTypes.includes(ssoType);
      const { hideLogoutButton: backendHidesLogoutButton } = isNativeMobile
        ? clientFeaturesNative
        : clientFeaturesWeb;
      if (backendHidesLogoutButton || frontendHidesLogoutButton) {
        showLogoutButton = false;
      }
    }
    return showLogoutButton;
  },
);

const selectSsoMigrationModalProps = () => createSelector(
  makeSelectMain('targetedUser'),
  makeSelectBuildType(),
  ({ ssoMigration }, buildType) => ({
    isOpen: !isEmpty(ssoMigration) && buildType !== 'mobile-web',
    ...ssoMigration,
  }),
);

const getError = (prop) => createSelector(
  makeSelectMain('error'),
  (error) => error[prop],
);

const getSsoClientImageProps = () => createSelector(
  getMainClientInfo('ssoClientImage'),
  makeSelectMobile('isMobile'),
  ({ image, type }, isMobile) => ({
    height: isMobile ? 120 : 162,
    source: { uri: `data:${type};base64, ${image}` },
    width: isMobile ? 287 : 310,
  }),
);

/**
 * Get a dictionary of hashedLoanNumber:loanData key:value pairs. Assists with lookup of loan data
 * based on hashedLoanNumber rather than having to manually loop through the 'main.loans' array.
 */
const getLoanDataDictionary = () => createSelector(
  makeSelectMain('loans'),
  (loans) => {
    if (!isEmpty(loans)) {
      return loans.reduce((acc, loanData) => {
        const { hashedLoanNumber } = loanData;
        return {
          ...acc,
          [hashedLoanNumber]: loanData,
        };
      }, {});
    }
    return {};
  },
);

/**
 * Get a dictionary of hashedLoanNumber:unmaskedLoanNumber key:value pairs. Simplified version of
 * getLoanDataDictionary selector.
 */
const getLoanNumberDictionary = () => createSelector(
  makeSelectMain('loans'),
  (loans) => {
    if (!isEmpty(loans)) {
      return loans.reduce((acc, { hashedLoanNumber, loanNumber }) => ({
        ...acc,
        [hashedLoanNumber]: loanNumber,
      }), {});
    }
    return {};
  },
);

const getSelectedLoanPropertyState = () => createSelector(
  makeSelectMain('loans'),
  makeSelectMain('selectedLoan'),
  (loans, selectedLoan) => {
    if (loans.length) {
      const selectedLoanData =
        loans.find(({ hashedLoanNumber }) => selectedLoan === hashedLoanNumber);
      if (selectedLoanData.header) {
        const { header: { stateCode } = {} } = selectedLoanData || {};
        return STATE_ABBR_DICTIONARY[stateCode] || '';
      }
    }
    return '';
  },
);

const selectTransferBannerText = () => createSelector(
  makeSelectMain('transferStatus'),
  ({ respaLetterId, servicingSoldDate }) => {
    const isAfterSoldDate = servicingSoldDate && moment().isAfter(servicingSoldDate);
    if (respaLetterId) {
      if (isAfterSoldDate) {
        return POST_TRANSFER_RESPA_BANNER_TEXT;
      }
      return PRE_TRANSFER_RESPA_BANNER_TEXT;
    }
    if (isAfterSoldDate) {
      return POST_TRANSFER_BANNER_TEXT;
    }
    return PRE_TRANSFER_BANNER_TEXT;
  },
);

const selectTransferStatus = (prop) => createSelector(
  makeSelectMain('transferStatus'),
  (transferStatus) => transferStatus[prop],
);

const selectIsPastServicingPaymentCutOffDate = () => createSelector(
  makeSelectMain('transferStatus'),
  ({ servicingPaymentCutOffDate }) => {
    if (servicingPaymentCutOffDate) {
      return moment.utc().isSameOrAfter(moment.utc(servicingPaymentCutOffDate));
    }
    return false;
  },
);

const selectDisplayTransferBanner = () => createSelector(
  makeSelectMain('transferStatus'),
  selectMainStatus('fetchTransferStatus'),
  (
    { servicingPaymentCutOffDate, servicingSoldDate },
    fetchTransferStatus,
  ) => {
    if (servicingPaymentCutOffDate && servicingSoldDate) {
      const isNotPastCurrentTime =
        moment.utc().isBefore(moment.utc(servicingPaymentCutOffDate)) &&
        moment.utc(servicingPaymentCutOffDate).isBefore(moment.utc(servicingSoldDate));
      return fetchTransferStatus === 'succeeded' && !isNotPastCurrentTime;
    }
    return false;
  },
);

const selectDocUploadStatusNotifications = () => createSelector(
  makeSelectMain('loans'),
  makeSelectMain('selectedLoan'),
  selectCustomerServicePhoneNumber(),
  selectIntl,
  (
    loans,
    selectedLoan,
    customerServicePhoneNumber,
    { formatMessage },
  ) => {
    const { additionalInformation: { uploadedDocumentStatus } = {} } =
      loans.find(({ hashedLoanNumber }) => hashedLoanNumber === selectedLoan) || {};

    const { availableDocuments } = uploadedDocumentStatus || {};
    if (!availableDocuments) return [];

    const sortedAvailableDocuments = [...availableDocuments]
      .sort((a, b) => moment(b.updatedAt).diff(moment(a.updatedAt)));

    return sortedAvailableDocuments.map(({
      message,
      status,
      updatedAt,
    }) => {
      let content;
      let id; // used for finding the most recent failed upload notification in well
      let hasFailed = false;

      if (status === 'Failed') {
        // eslint-disable-next-line no-plusplus
        if (!hasFailed) id = MOST_RECENT_FAILED_UPLOAD;
        hasFailed = true;

        content = [
          {
            inlineBlocks: [
              {
                text: `${message} For assistance, please contact customer service at `,
                type: 'span',
              },
              {
                data: {
                  mail: `tel:${customerServicePhoneNumber}`,
                  url: '',
                },
                text: customerServicePhoneNumber,
                type: 'link',
              },
            ],
            type: 'paragraph',
          }];
      } else {
        content = message
          ? [{ inlineBlocks: [{ text: message, type: 'span' }], type: 'paragraph' }]
          : [];
      }

      return {
        content,
        date: formatDate(updatedAt, 'UTC', 'MMMM D, YYYY'),
        id,
        name: formatMessage({ id: DOC_UPLOAD_STATUS_TITLE_DICT[status] }),
      };
    });
  },
);

const selectTransferStatusBannerProps = () => createSelector(
  selectDisplayTransferBanner(),
  selectTransferBannerText(),
  selectTransferStatusPhoneNumber(),
  (
    shouldTransferBannerDisplay,
    transferStatusBanner,
    transferServiceNumber,
  ) => ({
    shouldTransferBannerDisplay,
    transferStatusBannerText: interpolate(
      transferStatusBanner.text,
      { phoneNumber: transferServiceNumber },
    ),
    transferStatusBannerTitle: transferStatusBanner.title,
  }),
);

const selectTransferStatusPhoneNumber = () => createSelector(
  selectTransferStatus('insuranceMortgageeClausePhoneNumber'),
  (phoneNumber) => formatPhoneNumber(phoneNumber) || GENERIC_CUSTOMER_SERVICE_PHONE_NUMBER,
);

const selectPreLoginBannersToRender = () => createSelector(
  makeSelectMain('treatments'),
  makeSelectMain('dismissedBanners'),
  (treatments, dismissedBanners) => Object.entries(treatments).reduce((acc, [key, value]) => {
    if (!Object.keys(PRELOGIN_ALERT_BANNER_PROPS).includes(key)) return acc;
    const { bannerName, ...restProps } =
      PRELOGIN_ALERT_BANNER_PROPS[key];
    const { treatment } = value;
    if (bannerName === 'outageBanner' && treatment === 'on') {
      const { config } = treatments[SPLIT_OUTAGE_BANNER];
      const { body, title } = config || {};
      return [...acc, {
        bannerName,
        heading: title || '',
        messageText: body || '',
        ...restProps,
      }];
    }
    if (treatment !== 'on' || dismissedBanners.includes(bannerName)) {
      return acc;
    }
    return [...acc, PRELOGIN_ALERT_BANNER_PROPS[key]];
  }, []),
);

const selectDesktopPreLoginBanners = () => createSelector(
  selectPreLoginBannersToRender(),
  (banners) => {
    const { dismissibleBanners, nonDismissibleBanners } = banners.reduce((acc, banner) => {
      const { bannerType } = banner;
      if (bannerType === 'nonDismissible') {
        acc.nonDismissibleBanners.push(banner);
      } else {
        acc.dismissibleBanners.push(banner);
      }
      return acc;
    }, { dismissibleBanners: [], nonDismissibleBanners: [] });
    // sort banners by date added - newest first
    dismissibleBanners.sort((a, b) => b.dateAdded - a.dateAdded);

    return { dismissibleBanners, nonDismissibleBanners };
  },
);

const selectMobilePreLoginBanners = () => createSelector(
  selectPreLoginBannersToRender(),
  (banners) => getTopAndBottomPreLoginBanners(banners),
);

const selectIsZendeskChatbotTreatmentOn = () => createSelector(
  makeSelectMain('treatments'),
  (treatments) => {
    const { treatment } = treatments[SPLIT_ZENDESK_CHATBOT] || {};
    return treatment === 'on';
  },
);

const selectShouldRenderZendeskChatbot = () => createSelector(
  selectIsZendeskChatbotTreatmentOn(),
  getIsLoanAvailable(),
  selectInstantPostLoginModalViewName(),
  makeSelectMain('instantPostLoginModal'),
  makeSelectMain('chatbotWindowStatus'),
  (
    isZendeskChatbotTreatmentOn,
    isLoanAvailable,
    instantPostLoginModalViewName,
    { isEnabled },
    chatbotWindowStatus,
  ) => (
    isZendeskChatbotTreatmentOn
    && isLoanAvailable
    && (!instantPostLoginModalViewName || !isEnabled)
    && chatbotWindowStatus !== CHATBOT_WINDOW_STATUS_ERROR
  ),
);

/**
 * @description Selects whether to render element if in a non-sso session, or if
 * sso client has `hasNativeSso` flag set to true
 */

const selectShouldRenderIfNonSsoOrhasNativeSso = () => createSelector(
  getIsSso(),
  getMainClientInfo('hasNativeSso'),
  (isSso, hasNativeSso) => !isSso || hasNativeSso,
);

/**
 * If customHeader is provided and displayLogoSeparateFromCustomHeader is false,
 * do not show the nav bar logo
 */
const selectShouldShowNavBarLogo = () => createSelector(
  getMainClientInfo('displayLogoSeparateFromCustomHeader'),
  getMainClientInfo('customHeader'),
  (displayLogoSeparateFromCustomHeader, customHeader) => {
    if (customHeader && !displayLogoSeparateFromCustomHeader) return false;
    return true;
  },
);

const selectUnavailableFeaturesTreatment = () => createSelector(
  makeSelectMain('treatments'),
  (treatments) => {
    const { config, treatment } = treatments[SPLIT_UNAVAILABLE_FEATURES] || {};
    if (treatment === 'on') {
      const { features = [] } = config || {};
      return features;
    }
    return [];
  },
);

/**
 * Default selector used by Main
 */
const makeSelectMain = (prop) => createSelector(
  selectMainDomain,
  (mainState) => mainState[prop],
);

export default makeSelectMain;
export {
  getAccountModalProps,
  getChatbotQueryParams,
  getClientFontToUse,
  getError,
  getHasMultiLoans,
  getInactiveLoanRedirectUrl,
  getIsLoanAvailable,
  getIsSso,
  getLoanAddresses,
  getLoanDataDictionary,
  getLoanNumberDictionary,
  getLoansIsPrimaryStatus,
  getMainClientAuthProps,
  getMainClientInfo,
  getMainNotifications,
  getMostRecentDocUploadNotificationFailure,
  getNextPaymentDueDetails,
  getPaymentMethod,
  getProfileCurrentSetting,
  getSelectedLoanAddress,
  getSelectedLoanData,
  getSelectedLoanMaskedLoanNumber,
  getSelectedLoanPropertyState,
  getSelectedLoanUnmaskedLoanNumber,
  getSelectLoanProps,
  getSsoClientImageProps,
  getSsoType,
  getUserDisplayName,
  getWebSsoLogoutType,
  selectBannerError,
  selectChatbotWindowStatus,
  selectClientDesktopFeatures,
  selectClientMobileFeatures,
  selectCustomerServicePhoneNumber,
  selectDesktopPreLoginBanners,
  selectDesktopThemeBorders,
  selectDesktopThemeColors,
  selectDisplayTransferBanner,
  selectDocUploadStatusNotifications,
  selectHasChapterSevenBankruptcy,
  selectHasUnreadDocUploadStatusNotifications,
  selectInstantPostLoginModalViewProps,
  selectIsMurabahaAccount,
  selectIsNYCLanguagePreferenceModalRequired,
  selectIsPaperlessBillingModalAllowed,
  selectIsPastServicingPaymentCutOffDate,
  selectLanguage,
  selectLanguagePreferenceProps,
  selectMainDomain,
  selectMainStatus,
  selectMobilePreLoginBanners,
  selectMobileThemeColors,
  selectPaperlessBillingProps,
  selectPreLoginBannersToRender,
  selectShouldRenderIfNonSsoOrhasNativeSso,
  selectShouldRenderZendeskChatbot,
  selectShouldShowNavBarLogo,
  selectSsoMigrationModalProps,
  selectTransferBannerText,
  selectTransferStatus,
  selectTransferStatusBannerProps,
  selectTransferStatusPhoneNumber,
  selectUnavailableFeaturesTreatment,
};
